import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyInput,
  SkyMenu,
  SkyMessageBar,
  SkyText,
  SkyTooltip,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { EntitySelectedFilter, EntityType, EntityTypeFilter } from "@shared/types/dataflows";
import type { SchemaChangeTableCounts } from "@shared/types/schemaChanges";
import {
  type TableNameCase,
  type TableNaming,
  type WarehouseDestination,
} from "@shared/warehouse/targetTableName";
import { nextEditingName } from "@/components/connections/nameEditing";
import { TableNameLine } from "@/components/connections/TableNameLine";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { AddIcon, MoreActionsIcon, RetryIcon, SearchIcon } from "@/lib/icons";
import { pixelColumnLayout } from "@/lib/grid";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { stopRowSelection } from "@/lib/gridRow";
import { tableBadge } from "./schemaChangeCopy";
import type { EntitySelectionConfig } from "./types";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { BREAKABLE_NAME_CLASS, breakableName } from "@/lib/breakableName";
import { ZoneCaption } from "@/components/shell/ZoneCaption";

/**
 * Choosing what to import (FR-44, UX-4).
 *
 * A rewrite of skypoint-studio's `EntityTableV2.tsx`, not a port: that file is
 * 675 lines of Fluent v9 `Table*` primitives, and this repository's grid is
 * `SkyDataGrid`. Its behaviour is kept — search, the all/selected/unselected
 * view, select-all with an indeterminate state, and per-entity inline editing —
 * and its scale problem is fixed.
 *
 * **This component holds one page and nothing else.** The catalogue can be
 * 7,000 tables (legacy issues A1/A2) and lives on the server; the editor pages
 * and searches it through `GET /dataflows/:id/entities` (or, for a connection
 * that is not saved yet, through the list the validator just returned) and hands
 * this grid the page, the match count and the overall counts. Every control here
 * reports an intent — a page, a settled search, a view, a tick on THIS page, a
 * setting on one row, "select every match" — and the editor decides what that
 * means for its draft. That is what makes the same grid correct against a
 * server page and against an in-memory list.
 *
 * **The header checkbox means "every entity on this page."** `SkyDataGrid` scopes
 * its own select-all to the rows it holds and REPLACES the selection with them,
 * so `onToggle` is reported with only the names on this page and nothing off-page
 * moves. "Select all N matching" is a separate, explicitly-labelled action that
 * reaches every page of the current search, because a control that silently
 * means one of two scopes is worse than two controls.
 *
 * **The search box settles before it asks.** Every keystroke paints; the
 * `onSearchChange` intent fires once the typing pauses (`SEARCH_DEBOUNCE_MS`),
 * so a 7,000-row source is not queried per character.
 */

export interface EntityRow {
  name: string;
  displayName: string | null;
  sourceSchema: string | null;
  sourceTable: string | null;
  isSelected: boolean;
  incrementalEnabled: boolean;
  watermarkColumn: string | null;
  keyColumns: string[];
  /** #1465: a table or a view, as the last validation listed it; absent reads as a table. */
  entityType?: EntityType;
  /** Product gaps row 2: "Data through", the table's own watermark; absent until it lands. */
  refreshedThrough?: string;
  /** Review r2: the Table name case its first load named it with; absent until then. */
  tableNameCase?: TableNameCase;
  /**
   * Round 7 (instance 0020): the table's own warehouse name; absent when it has none. Null is a
   * stored name the editor's draft clears.
   */
  targetName?: string | null;
  /**
   * #2428: a GA4 report's definition, as stored or as the drawer built it. A reader below
   * `dataflows.write` reads the property and the filter withheld (null, named in `withheld`).
   */
  definition?: ReportDefinitionValue;
  /** #2428: the report's table is rebuilt from its current definition on the next import. */
  rebuildPending?: boolean;
}

/** #2428: a report's definition on a row (`DataflowEntityDefinitionView` without its kind). */
export interface ReportDefinitionValue {
  propertyId: string | null;
  dimensions: string[];
  metrics: string[];
  dimensionFilter?: Record<string, unknown> | null;
  withheld?: Array<"propertyId" | "dimensionFilter">;
}

/**
 * Lead round: a table has landed once it has a bound or a first-load case, the facts the server
 * reads before it lets a table's own name change (`DataflowService.writeTargetNames`).
 */
export function entityHasLanded(row: EntityRow): boolean {
  return Boolean(row.refreshedThrough || row.tableNameCase);
}

/**
 * Owner 2026-10-01 23:40: a ticked table that would land in the same warehouse table as another
 * ticked one (`findTargetNameCollisions` in `shared/warehouse/targetTableName.ts`).
 */
export interface EntityNameCollision {
  otherEntityName: string;
  /** The sentence the server's 409 `TABLE_NAME_TAKEN` uses too. */
  sentence: string;
}

/** Product gaps row 2: one table run on its own, from its row menu. */
export interface EntityTableActions {
  onResync: (name: string) => void;
  /**
   * #1077: "Read changes since...", left out (with this) for a connector that cannot import
   * incrementally: its tables have no watermark, so a since date would bound nothing.
   */
  onReadSince?: (name: string) => void;
}

export type EntityView = EntitySelectedFilter;

export const SEARCH_DEBOUNCE_MS = 250;

export interface EntityTableProps {
  /** The current page, with the editor's unsaved changes already applied. */
  rows: EntityRow[];
  /** Rows matching the current search and view across every page. */
  total: number;
  /** Every discovered entity, whatever the search. */
  entityCount: number;
  /** Every selected entity, whatever the search, unsaved changes included. */
  selectedCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** The settled search text the page was fetched for. */
  search: string;
  onSearchChange: (search: string) => void;
  view: EntityView;
  onViewChange: (view: EntityView) => void;
  /**
   * #1465: the Type filter (All, Tables, Views). The control shows only for a connector that lists
   * views (the Type column) and when the page passes a handler.
   */
  entityType?: EntityTypeFilter;
  onEntityTypeChange?: (entityType: EntityTypeFilter) => void;
  /** How many of `entityCount` are views, and how many of the `selectedCount` selected. */
  viewCount?: number;
  selectedViewCount?: number;
  /** A page or a search is on its way; the grid stays painted with the last one. */
  isLoading?: boolean;
  /** Ticks or unticks the named rows, which are always rows on this page. */
  onToggle: (names: string[], isSelected: boolean) => void;
  /** One row's per-entity settings changed. */
  onRowChange: (name: string, patch: Partial<EntityRow>) => void;
  /**
   * "Select all N matching" / "Clear all N matching": every row the current search
   * and view match, on every page. Absent when the connector does not allow it.
   */
  onSelectAllMatching?: (isSelected: boolean) => void;
  /**
   * #1129: the bulk action writes at once (a saved connection), so its label says it saves:
   * "Select all 7 and save". On a new connection it only changes the form, like a tick.
   */
  bulkSaves?: boolean;
  /**
   * #1098 review: why the bulk action that saves is off, as the save bar's Save is (an import
   * running, a Validate running); shown as its tooltip. Absent while it can save.
   */
  bulkDisabledReason?: string;
  /** The connector's own entity-selection configuration, when it declares one. */
  config?: EntitySelectionConfig | null;
  /** FR-44: the per-entity incremental flag only exists where the connector supports it. */
  supportsIncrementalPerEntity: boolean;
  readOnly?: boolean;
  /** Opens the Add entity drawer. Absent unless the connector supports hand-named entities. */
  onAddEntity?: () => void;
  /** When the list was last refreshed by a successful validation. */
  listedAt?: string | null;
  /**
   * Product gaps row 2: "Data through" per table and a row menu on selected tables (Re-sync this
   * table, Read changes since). Absent, the grid is as it was: the host passes it only for a
   * saved, ready connection, a reader with `runs.start`, and a deployment with table runs on.
   */
  tableActions?: EntityTableActions;
  /**
   * Product gaps row 7: open schema changes per table (`GET /schema-changes/summary`). With any, a
   * "Schema changes" column shows each such table's badge, which opens its change log. Absent or
   * empty, the grid is as it was: the common case, a connection whose columns did not change.
   */
  schemaCounts?: ReadonlyMap<string, SchemaChangeTableCounts>;
  onOpenSchemaChanges?: (name: string) => void;
  /**
   * Owner 2026-10-01 23:40: ticked rows that would share a warehouse table, by entity name. Each
   * such row says so under its name, and a message bar above the grid holds the full sentence.
   */
  nameCollisions?: ReadonlyMap<string, EntityNameCollision>;
  /**
   * Round 7: the instance's destination, to spell a table's own warehouse name the way it is
   * stored ("Lands as CRM_CUSTOMERS"). Absent is Snowflake, the one loader's destination.
   */
  destination?: WarehouseDestination;
  /**
   * Lead round (backlog "A per-table name override"): how the connection names its tables. With
   * it, a selected table that has not landed has a "Warehouse table name" input whose placeholder
   * is the name it lands as without one. Absent, there is no input.
   */
  naming?: TableNaming;
  /** Lead round: the server's refusal of a table's own name (409 `TABLE_NAME_TAKEN`), by entity. */
  targetNameErrors?: ReadonlyMap<string, string>;
}

/** The Type filter's button labels. */
const TYPE_LABELS: Readonly<Record<EntityTypeFilter, string>> = {
  all: "All types",
  table: "Tables",
  view: "Views",
};

/** The selection summary's split: "3 tables and 2 views". */
function selectionSplit(selected: number, selectedViews: number): string {
  const views = Math.min(selectedViews, selected);
  const tables = selected - views;
  const noun = (count: number, word: string) =>
    `${count.toLocaleString()} ${word}${count === 1 ? "" : "s"}`;
  return `${noun(tables, "table")} and ${noun(views, "view")}`;
}

/** The view toggle's button labels. */
const VIEW_LABELS: Readonly<Record<EntityView, string>> = {
  all: "All",
  selected: "Selected",
  unselected: "Unselected",
};

/**
 * Fixes wave 3 (post-release check 2b, 07c-connection-tables.png): the grid is `table-fixed` with
 * percentage widths, so in the connection editor's half-width pane at 1440 px every column shrank
 * to fit and "Data through" got 63 px: its header ran into "Actions" and a date wrapped to five
 * lines. Both now have pixel widths (a date and time on one line; the menu button and its
 * header), and the table gets a floor so no percent column goes below the room its header
 * needs. Below the floor the grid's region scrolls sideways, as the other floored grids do.
 * Tailwind's preflight makes every cell `box-sizing: border-box`, so each width below already holds
 * the header's `px-4` (32 px).
 *
 * Round 4 polish (2026-10-01): the widths are now pinned against the harness's Chromium measures
 * in both fonts (`HEADER_TEXT_WIDTH`, `VALUE_WIDTH` in `client/src/tests/gridLayout.ts`), the
 * larger of the app's stack and Inter. A time to the seconds at 12 px is 165 px in the app stack
 * (Inter was the one measured before, about 148), so "Data through" is 192 px: 168 px for the
 * value in the cell's 24 px padding. "Actions" (67 px) and "Schema" (71 px) need 104 px with the
 * header's 32, and "Schema changes" (152 px) 184.
 *
 * Walk 2026-10-02, D10 (Mouseflow at 1280 px: "Data through" cut under the pinned Actions, table
 * names cut): Data through is 128 px, its time on two lines at the comma ("May 28, 2026," 86 px
 * at 12 px) and its header on two at the space ("through" 73 px, `[&_th]:whitespace-normal`),
 * and a table's name wraps rather than truncates. The plain floor is 548 px, inside the 552 px
 * pane at 1280 px, so every column is in view there.
 */
const DATA_THROUGH_WIDTH = 128;
const ACTIONS_WIDTH = 104;
/**
 * #1514: the unpin-while-scrolling rule (#1143) is for a grid whose scroll is a few pixels (the
 * plain floor, 552 px, in the 552 px pane at 1280). With the Type column (640 px) or Incremental
 * the floor is well past the pane, so the grid scrolls at rest and the unpin left Actions off
 * screen until the scroll ended. Past this floor Actions stays pinned.
 */
const UNPIN_MAX_FLOOR = 600;
/** The narrowest each percent column may get: its header on one line, or a readable value. */
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  // Round 6: 164, not 160. With Source gone every floor (612, 772, 956) would otherwise be an
  // exact multiple of 8, and the two-decimal percentages then leave a column a hundredth short.
  name: 164,
  sourceSchema: 104,
  entityType: 88,
  incrementalEnabled: 160,
  schemaChanges: 184,
};

/**
 * The pixel-column layout (`pixelColumnLayout`): every percent column's share is set in proportion
 * to its minimum, so the floor is as low as it can be (616 px with no incremental column, 776 px
 * with it, since round 6 dropped Source), which is what decides how far the pane scrolls. Round 6
 * (walk W16) gives the Tables pane seven twelfths of the editor: 645.75 px at 1440, past the plain
 * floor, so every column is in view. Below a floor (1280 px, or with Incremental) the Actions
 * column is pinned to the grid's right edge (`.grid-pinned-last` in `client/src/index.css`, fixes
 * wave 4): the rest scrolls under it.
 */
function withPixelColumns(columns: SkyDataGridColumn<EntityRow>[]): {
  columns: SkyDataGridColumn<EntityRow>[];
  floor: number;
} {
  return pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: true });
}

/**
 * The name cell's tooltip: the words on screen, then the table's own name in parentheses when a
 * display name differs from it (a display name is free text, `AddEntityDrawer`), so a truncated
 * cell's tooltip always holds what it cut.
 */
function nameTitle(row: EntityRow): string {
  const shown = row.displayName || row.name;
  return shown === row.name ? shown : `${shown} (${row.name})`;
}

/** One bar naming every warehouse-table collision once; nothing when there is none. */
function NameCollisionsBar({
  nameCollisions,
}: {
  nameCollisions: EntityTableProps["nameCollisions"];
}) {
  if (!nameCollisions || nameCollisions.size === 0) return null;
  return (
    <SkyMessageBar
      intent="error"
      testId="entity-name-collisions"
      message={[...new Set([...nameCollisions.values()].map((item) => item.sentence))].join(" ")}
    />
  );
}

/** The bulk button's words: "Select all 1,234", "Select 12 matching", or "Clear ..." for a clear. */
function bulkActionLabel(bulkSelects: boolean, filtered: boolean, total: number): string {
  const matchingLabel = filtered
    ? `${total.toLocaleString()} matching`
    : `all ${total.toLocaleString()}`;
  return bulkSelects ? `Select ${matchingLabel}` : `Clear ${matchingLabel}`;
}

export function EntityTable({
  rows,
  total,
  entityCount,
  selectedCount,
  page,
  pageSize,
  onPageChange,
  search,
  onSearchChange,
  view,
  onViewChange,
  entityType = "all",
  onEntityTypeChange,
  viewCount = 0,
  selectedViewCount = 0,
  isLoading = false,
  onToggle,
  onRowChange,
  onSelectAllMatching,
  bulkSaves = false,
  bulkDisabledReason,
  config,
  supportsIncrementalPerEntity,
  readOnly = false,
  onAddEntity,
  listedAt,
  tableActions,
  schemaCounts,
  onOpenSchemaChanges,
  nameCollisions,
  destination = "snowflake",
  naming,
  targetNameErrors,
}: EntityTableProps) {
  const plural = config?.entityLabelPlural ?? "entities";
  const singular = config?.entityLabelSingular ?? "entity";

  // The typed value paints at once; the settled value is what the editor searches. When
  // the parent resets `search` to something this box never emitted (a reseed after a
  // save, a discard), the box follows it rather than re-emitting its stale text.
  const [typed, setTyped] = useState(search);
  const zone = useDisplayTz();
  const emitted = useRef(search);
  useEffect(() => {
    if (search !== emitted.current) {
      emitted.current = search;
      setTyped(search);
      return undefined;
    }
    if (typed === search) return undefined;
    const timer = setTimeout(() => {
      emitted.current = typed;
      onSearchChange(typed);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // `onSearchChange` is an intent handler, not a value; re-arming on its identity would
    // restart the debounce on every render of the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, search]);

  // #1610: the one row whose warehouse name editor is open. Opening another closes it as Cancel
  // does, and it closes with its row (unticked, or off this page).
  const [editingName, setEditingName] = useState<string | null>(null);
  if (editingName !== null && !rows.some((row) => row.name === editingName && row.isSelected)) {
    setEditingName(null);
  }

  const pageSelectedIds = rows.filter((row) => row.isSelected).map((row) => row.name);

  // #1465: a catalogue that lists views (the definition names a `tableType` column) says which each
  // row is. A connector that lists tables only shows no Type column.
  const showsEntityType = config?.columns?.some((column) => column.id === "tableType") === true;

  const changeNameEditing = useCallback((rowName: string, open: boolean) => {
    setEditingName((current) => nextEditingName(current, rowName, open));
  }, []);

  const columns = useMemo<SkyDataGridColumn<EntityRow>[]>(() => {
    const list: SkyDataGridColumn<EntityRow>[] = [
      {
        key: "name",
        header: singular,
        width: supportsIncrementalPerEntity ? "40%" : "55%",
        // Round 4 polish: `block` as well as `truncate` (an inline span ignores `truncate`, and a
        // `renderCell` cell does not clip, so a long name ran over the next column). The title
        // holds the words on screen, then the table's own name when a display name differs.
        renderCell: (_value, row) => {
          const collision = row.isSelected ? nameCollisions?.get(row.name) : undefined;
          const name = (
            <SkyText
              weight="medium"
              block
              className={BREAKABLE_NAME_CLASS}
              title={nameTitle(row)}
              testId={`entity-name-${row.name}`}
            >
              {breakableName(row.displayName || row.name)}
            </SkyText>
          );
          const ownName = (
            <TableNameLine
              row={row}
              naming={naming}
              destination={destination}
              readOnly={readOnly}
              landed={entityHasLanded(row)}
              reserveRight={tableActions ? ACTIONS_WIDTH + 8 : 8}
              serverIssue={targetNameErrors?.get(row.name)}
              editing={editingName === row.name}
              onEditingChange={(open) => changeNameEditing(row.name, open)}
              onChange={(targetName) => onRowChange(row.name, { targetName })}
            />
          );
          if (!collision) {
            return (
              <div className="flex min-w-0 flex-col gap-1">
                {name}
                {ownName}
              </div>
            );
          }
          return (
            <div className="flex min-w-0 flex-col gap-1">
              {name}
              {ownName}
              <SkyText
                size={200}
                color="danger"
                block
                title={collision.sentence}
                testId={`entity-name-error-${row.name}`}
              >
                Same warehouse table as {collision.otherEntityName}
              </SkyText>
            </div>
          );
        },
      },
      {
        key: "sourceSchema",
        header: "Schema",
        width: "20%",
        renderCell: (_value, row) => (
          <SkyText color="muted" block truncate title={row.sourceSchema ?? undefined}>
            {row.sourceSchema ?? "—"}
          </SkyText>
        ),
      },
      // Round 6 (walk W16): no "Source" column. It repeated the table's own name (the name cell's
      // title holds the full name), and at 1440 px it pushed "Data through" under Actions.
    ];

    if (showsEntityType) {
      list.push({
        key: "entityType",
        header: "Type",
        width: "12%",
        renderCell: (_value, row) => (
          <SkyText color="muted" block testId={`entity-type-${row.name}`}>
            {row.entityType === "view" ? "View" : "Table"}
          </SkyText>
        ),
      });
    }

    // #1390 (finding 22): a reader who cannot tick has no checkbox column, so the grid showed a
    // selected table only as a tint. Say it in words, for every row, so selected is never colour alone.
    if (readOnly) {
      list.push({
        key: "isSelected",
        header: "Selected",
        width: "12%",
        renderCell: (_value, row) => (
          <SkyText
            color={row.isSelected ? undefined : "muted"}
            weight={row.isSelected ? "medium" : undefined}
            block
            testId={`entity-selected-state-${row.name}`}
          >
            {row.isSelected ? "Selected" : "Unselected"}
          </SkyText>
        ),
      });
    }

    if (supportsIncrementalPerEntity) {
      list.push({
        key: "incrementalEnabled",
        header: "Incremental",
        width: "25%",
        renderCell: (_value, row) => (
          <div className="flex flex-col gap-1">
            <SingleCheckbox
              label="Incremental"
              testId={`entity-incremental-${row.name}`}
              checked={row.incrementalEnabled}
              disabled={readOnly || !row.isSelected}
              onChange={(checked) =>
                onRowChange(row.name, {
                  incrementalEnabled: checked,
                  watermarkColumn: checked ? row.watermarkColumn : null,
                })
              }
            />
            {row.incrementalEnabled ? (
              <SkyInput
                size="small"
                testId={`entity-watermark-${row.name}`}
                aria-label={`Watermark column for ${row.name}`}
                placeholder="Watermark column"
                value={row.watermarkColumn ?? ""}
                disabled={readOnly}
                onChange={(event) =>
                  onRowChange(row.name, { watermarkColumn: event.target.value || null })
                }
              />
            ) : null}
          </div>
        ),
      });
    }

    if (schemaCounts && schemaCounts.size > 0) {
      list.push({
        key: "schemaChanges",
        header: "Schema changes",
        width: "16%",
        renderCell: (_value, row) => {
          const badge = tableBadge(schemaCounts.get(row.name));
          if (!badge) return null;
          const content = (
            <SkyBadge
              appearance="tint"
              color={badge.color}
              testId={`entity-schema-badge-${row.name}`}
            >
              {badge.label}
            </SkyBadge>
          );
          return onOpenSchemaChanges ? (
            // A click on the badge opens the log; it must not tick or untick the row.
            <div onClick={stopRowSelection}>
              <SkyButton
                appearance="subtle"
                size="small"
                aria-label={`Schema changes of ${row.name}: ${badge.label}`}
                testId={`entity-schema-open-${row.name}`}
                onClick={() => onOpenSchemaChanges(row.name)}
              >
                {content}
              </SkyButton>
            </div>
          ) : (
            content
          );
        },
      });
    }

    if (tableActions) {
      list.push(
        {
          key: "refreshedThrough",
          header: "Data through",
          width: `${DATA_THROUGH_WIDTH}px`,
          renderCell: (_value, row) => (
            <SkyText
              size={200}
              block
              color={row.refreshedThrough ? undefined : "muted"}
              testId={`entity-data-through-${row.name}`}
              title={instantHoverTitle(row.refreshedThrough, zone)}
            >
              {row.refreshedThrough
                ? formatInstant(row.refreshedThrough, zone, "seconds")
                : "Not yet imported"}
            </SkyText>
          ),
        },
        {
          key: "actions",
          header: "Actions",
          width: `${ACTIONS_WIDTH}px`,
          renderCell: (_value, row) =>
            row.isSelected ? (
              // 2026-09-29: a menu item's click must not reach the row and untick the table.
              <div onClick={stopRowSelection}>
                <SkyMenu
                  align="end"
                  items={[
                    {
                      key: "resync",
                      label: "Re-sync this table",
                      icon: <RetryIcon className="size-4" />,
                      onClick: () => tableActions.onResync(row.name),
                    },
                    ...(tableActions.onReadSince
                      ? [
                          {
                            key: "since",
                            label: "Read changes since...",
                            onClick: () => tableActions.onReadSince?.(row.name),
                          },
                        ]
                      : []),
                  ]}
                  trigger={
                    <SkyButton
                      appearance="subtle"
                      size="small"
                      icon={MoreActionsIcon}
                      aria-label={`Import actions for ${row.name}`}
                      testId={`entity-actions-${row.name}`}
                    />
                  }
                />
              </div>
            ) : null,
        },
      );
    }

    return list;
  }, [
    singular,
    supportsIncrementalPerEntity,
    showsEntityType,
    readOnly,
    onRowChange,
    tableActions,
    schemaCounts,
    onOpenSchemaChanges,
    zone,
    nameCollisions,
    destination,
    naming,
    targetNameErrors,
    editingName,
    changeNameEditing,
  ]);

  // Only the grid with the pixel columns is rebalanced and floored; without them it is as it was.
  const layout = useMemo(
    () => (tableActions ? withPixelColumns(columns) : { columns, floor: null }),
    [tableActions, columns],
  );
  const floor = layout.floor;
  // The pinned Actions column's edge line shows only while columns scroll under it.
  const pinnedEdge = usePinnedColumnEdge<HTMLDivElement>(floor !== null);

  // Each row is the containing block of its warehouse name line (`TableNameLine`), which spans the
  // row under the table's own name; the other cells sit at the top of the row, above that line.
  const gridClassName = `[&_tbody_tr]:relative [&_tbody_td]:align-top! ${
    floor
      ? `grid-pinned-last ${floor <= UNPIN_MAX_FLOOR ? "grid-unpin-scrolling " : ""}[&_table]:min-w-(--entity-grid-floor) [&_th]:whitespace-normal`
      : ""
  }`;

  const filtered = search.trim() !== "" || view !== "all" || entityType !== "all";
  const typeCounts: Readonly<Record<EntityTypeFilter, number>> = {
    all: entityCount,
    table: Math.max(entityCount - viewCount, 0),
    view: viewCount,
  };
  // In the Selected view every match is ticked by definition, so the bulk action there is
  // the clear; everywhere else it is the select. One button, whose meaning the view states.
  const bulkSelects = view !== "selected";
  const bulkLabel = `${bulkActionLabel(bulkSelects, filtered, total)}${bulkSaves ? " and save" : ""}`;

  return (
    <div
      ref={pinnedEdge}
      className="flex flex-col gap-3"
      data-testid="entity-table"
      style={floor ? ({ "--entity-grid-floor": `${floor}px` } as CSSProperties) : undefined}
    >
      <div className="flex flex-wrap items-center gap-2">
        {config?.supportsSearch !== false ? (
          <div className="min-w-[200px] flex-1">
            <SkyInput
              testId="entity-search"
              aria-label={`Search ${plural.toLowerCase()}`}
              placeholder={`Search ${plural.toLowerCase()}`}
              value={typed}
              clearable
              contentBefore={<SearchIcon className="h-4 w-4" />}
              onChange={(event) => setTyped(event.target.value)}
              onClear={() => {
                setTyped("");
                emitted.current = "";
                onSearchChange("");
              }}
            />
          </div>
        ) : null}

        {config?.viewToggle !== false ? (
          <fieldset className="flex min-w-0 items-center gap-1" aria-label={`Show which ${plural}`}>
            {(["all", "selected", "unselected"] as const).map((option) => (
              <SkyButton
                key={option}
                size="small"
                appearance={view === option ? "secondary" : "subtle"}
                aria-pressed={view === option}
                testId={`entity-view-${option}`}
                onClick={() => onViewChange(option)}
              >
                {VIEW_LABELS[option]}
              </SkyButton>
            ))}
          </fieldset>
        ) : null}

        {showsEntityType && onEntityTypeChange ? (
          <fieldset className="flex min-w-0 items-center gap-1" aria-label="Show which type">
            {(["all", "table", "view"] as const).map((option) => (
              <SkyButton
                key={option}
                size="small"
                appearance={entityType === option ? "secondary" : "subtle"}
                aria-pressed={entityType === option}
                testId={`entity-type-${option}`}
                onClick={() => onEntityTypeChange(option)}
              >
                {TYPE_LABELS[option]} ({typeCounts[option].toLocaleString()})
              </SkyButton>
            ))}
          </fieldset>
        ) : null}

        {onAddEntity ? (
          <SkyButton
            appearance="secondary"
            icon={AddIcon}
            testId="entity-add"
            disabled={readOnly}
            onClick={onAddEntity}
          >
            Add {singular.toLowerCase()}
          </SkyButton>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SkyBadge appearance="tint" color="subtle" testId="entity-selected-count">
          {selectedCount.toLocaleString()} of {entityCount.toLocaleString()} selected
        </SkyBadge>
        {showsEntityType && viewCount > 0 ? (
          <SkyText size={200} color="muted" testId="entity-selected-split" aria-live="polite">
            {selectionSplit(selectedCount, selectedViewCount)} selected.
          </SkyText>
        ) : null}
        {filtered ? (
          <SkyText size={200} color="muted" testId="entity-matching-count" aria-live="polite">
            {total.toLocaleString()} matching
          </SkyText>
        ) : null}
        {onSelectAllMatching && config?.supportsSelectAll !== false && total > 0 && !readOnly ? (
          <BulkTooltip reason={bulkDisabledReason}>
            <SkyButton
              size="small"
              appearance="subtle"
              testId="entity-select-all-matching"
              disabled={isLoading || Boolean(bulkDisabledReason)}
              onClick={() => onSelectAllMatching(bulkSelects)}
            >
              {bulkLabel}
            </SkyButton>
          </BulkTooltip>
        ) : null}
        {/* #1348: the bulk button that saves is off with Save's reason, said in words as well as on hover. */}
        {onSelectAllMatching &&
        config?.supportsSelectAll !== false &&
        total > 0 &&
        !readOnly &&
        bulkDisabledReason ? (
          <SkyText size={200} color="muted" testId="entity-bulk-disabled-reason">
            {bulkDisabledReason}
          </SkyText>
        ) : null}
        {listedAt ? (
          <SkyText size={200} color="muted">
            Listed by the last successful validation.
          </SkyText>
        ) : null}
      </div>

      <NameCollisionsBar nameCollisions={nameCollisions} />

      <SkyDataGrid<EntityRow>
        testId="entity-grid"
        className={gridClassName}
        data={rows}
        columns={layout.columns}
        getRowId={(row) => row.name}
        loading={isLoading && rows.length === 0}
        selectionMode={readOnly ? "none" : "multiple"}
        selectedRows={pageSelectedIds}
        onSelectionChange={(selectedIds) => {
          // Reconcile against THIS page only: ids the grid did not see stay as
          // they are, so a select-all here cannot clear another page.
          const nowSelected = new Set(selectedIds.map(String));
          const ticked = rows.filter((row) => !row.isSelected && nowSelected.has(row.name));
          const unticked = rows.filter((row) => row.isSelected && !nowSelected.has(row.name));
          if (ticked.length > 0)
            onToggle(
              ticked.map((row) => row.name),
              true,
            );
          if (unticked.length > 0)
            onToggle(
              unticked.map((row) => row.name),
              false,
            );
        }}
        pagination
        page={page}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={onPageChange}
        emptyMessage={
          entityCount === 0
            ? `Validate the connection to list its ${plural.toLowerCase()}.`
            : `No ${plural.toLowerCase()} match that search.`
        }
      />
      {/* B-51 (round 9): "Data through" is in the reader's zone, said once. */}
      {tableActions && rows.length > 0 ? (
        <ZoneCaption testId="entity-zone-caption" className="mt-2" />
      ) : null}
    </div>
  );
}

/** The bulk button's reason for being off, as Save's tooltip says it (#1098 review). */
function BulkTooltip({ reason, children }: { reason: string | undefined; children: ReactNode }) {
  if (!reason) return <>{children}</>;
  return (
    <SkyTooltip content={reason}>
      <span className="inline-flex">{children}</span>
    </SkyTooltip>
  );
}
